import { Head, Link, router } from '@inertiajs/react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Breadcrumbs } from '@/components/ui/breadcrumbs';
import { ArrowLeft, Save } from 'lucide-react';
import { useState } from 'react';

interface SplitJob {
  id?: number;
  start_page?: number;
  end_page?: number;
  header_search_string?: string;
  split_rules?: string;
  status?: string;
  start_pages?: any;
  processing_job_id?: string;
}

interface Document {
  id: number;
  name: string;
  page_count?: number;
  split_jobs: SplitJob[];
}

interface SplitJobFormProps {
  document: Document;
  splitJob: SplitJob;
}

export default function SplitJobForm({ document, splitJob }: SplitJobFormProps) {
  const [formData, setFormData] = useState({
    start_page: splitJob.start_page || 1,
    end_page: splitJob.end_page || document.page_count,
    header_search_string: splitJob.header_search_string || '',
    split_rules: splitJob.split_rules || '',
    start_pages: splitJob.start_pages ? JSON.stringify(splitJob.start_pages, null, 2) : ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (field: string, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const url = splitJob.id 
        ? `/documents/${document.id}/split_jobs/${splitJob.id}`
        : `/documents/${document.id}/split_jobs`;
      
      const method = splitJob.id ? 'patch' : 'post';
      
      const submitData = {
        split_job: {
          start_page: Number(formData.start_page) || 1,
          end_page: Number(formData.end_page) || document.page_count,
          header_search_string: formData.header_search_string,
          split_rules: formData.split_rules,
          ...(formData.start_pages && { start_pages: formData.start_pages })
        }
      };

      router[method](url, submitData);
    } catch (error) {
      console.error('Error submitting split job:', error);
      setIsSubmitting(false);
    }
  };

  const pageTitle = splitJob.id ? 'Edit Split Job' : 'New Split Job';

  return (
    <>
      <Head title={`${pageTitle} - ${document.name}`} />
      
      <div className="container mx-auto p-6 max-w-4xl">
        <div className="space-y-6">
          {/* Breadcrumbs */}
          <Breadcrumbs opsConsole
            items={[
              { label: document.name, href: `/documents/${document.id}` },
              { label: pageTitle, current: true }
            ]}
          />

          {/* Header */}
          <div className="flex items-center justify-between">
            <h1 className="text-3xl font-bold">{pageTitle}</h1>
            <Button asChild variant="outline">
              <Link href={`/documents/${document.id}`}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Document
              </Link>
            </Button>
          </div>

          {/* Form */}
          <Card>
            <CardHeader>
              <CardTitle>Split Job Configuration</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="start_page">Start Page</Label>
                    <Input
                      id="start_page"
                      type="number"
                      value={formData.start_page}
                      onChange={(e) => handleInputChange('start_page', e.target.value === '' ? '' : parseInt(e.target.value) || '')}
                      min="1"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="end_page">End Page</Label>
                    <Input
                      id="end_page"
                      type="number"
                      value={formData.end_page}
                      onChange={(e) => handleInputChange('end_page', e.target.value === '' ? '' : parseInt(e.target.value) || '')}
                      min="1"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="header_search_string">Header Search String</Label>
                  <Input
                    id="header_search_string"
                    value={formData.header_search_string}
                    onChange={(e) => handleInputChange('header_search_string', e.target.value)}
                    placeholder="Enter header text to search for (e.g., Chapter, Section)"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="split_rules">Split Rules</Label>
                  <Textarea
                    id="split_rules"
                    value={formData.split_rules}
                    onChange={(e) => handleInputChange('split_rules', e.target.value)}
                    placeholder="Enter any specific rules for splitting the document"
                    rows={4}
                  />
                </div>

                {splitJob.id && (
                  <div className="space-y-2">
                    <Label htmlFor="start_pages">Start Pages (JSON)</Label>
                    <Textarea
                      id="start_pages"
                      value={formData.start_pages}
                      onChange={(e) => handleInputChange('start_pages', e.target.value)}
                      placeholder='{"1": "Chapter 1", "5": "Chapter 2"}'
                      rows={6}
                      className="font-mono text-sm"
                    />
                    <p className="text-sm text-gray-600">
                      JSON object mapping page numbers to section headers
                    </p>
                  </div>
                )}

                <div className="flex justify-end space-x-4">
                  <Button type="button" variant="outline" asChild>
                    <Link href={`/documents/${document.id}`}>Cancel</Link>
                  </Button>
                  <Button type="submit" disabled={isSubmitting}>
                    <Save className="h-4 w-4 mr-2" />
                    {isSubmitting ? 'Saving...' : 'Save Split Job'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
}
